import type * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Button from '@cloudscape-design/components/button';
import {
  effectiveCommercialMode,
  isUnpaidAssistedSale,
  PaymentProvider,
  type Subscription,
  type Tenant,
  CommercialMode,
} from '@etiquetachef/types';
import { DeleteTenantDialog } from '@/features/tenants/DeleteTenantDialog';
import { RestoreTenantDialog } from '@/features/tenants/RestoreTenantDialog';
import { SuspendDialog } from './SuspendDialog';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { ReactivateDialog } from './ReactivateDialog';
import { ApplyLifetimeFreeDialog } from './ApplyLifetimeFreeDialog';
import { RemoveLifetimeFreeDialog } from './RemoveLifetimeFreeDialog';
import { ReconcileSubscriptionDialog } from './ReconcileSubscriptionDialog';
import { PostponeDueDateDialog } from './PostponeDueDateDialog';
import { CreateCheckoutLinkDialog } from './CreateCheckoutLinkDialog';
import { ChangeCommercialModeDialog } from './ChangeCommercialModeDialog';
import { targetsFor } from './commercialModeTargets';
import { isCancellationScheduled } from './scheduledCancellation';

type Props = {
  tenant: Tenant;
  /** Optional — quando passada, habilita o toggle "Aplicar/Remover
   * isenção Lifetime Free" do PR G3/G4. */
  subscription?: Subscription | null;
  /** #289 — avisa a lista de links de pagamento para recarregar. */
  onCheckoutLinkCreated?: () => void;
};

type OpenDialog =
  | 'suspend'
  | 'reactivate'
  | 'delete'
  | 'restore'
  | 'applyLifetimeFree'
  | 'removeLifetimeFree'
  | 'cancelSubscription'
  | 'reconcileSubscription'
  | 'postponeDueDate'
  | 'checkoutLink'
  | 'commercialMode'
  | null;

/**
 * Faixa de ações do detalhe do tenant: suspender/reativar, excluir/restaurar,
 * faturas, conceder/remover cortesia, prorrogar vencimento, cancelar e
 * sincronizar assinatura, link de pagamento e troca de tipo comercial — cada
 * uma abre o diálogo correspondente.
 *
 * Origem: sem issue — commit efa5a8ca2, 2026-04-26 17:22 +0200 (Sprint 2).
 *
 * Por quê: toda precondição que o servidor recusaria está aqui como botão
 * desabilitado COM `disabledReason`, para o operador saber antes do clique.
 * Os predicados são os mesmos do servidor (`effectiveCommercialMode`,
 * `isUnpaidAssistedSale`, `targetsFor`), nunca uma cópia local.
 *
 * Contrato: `subscription` opcional — sem ela só suspender/reativar/excluir e
 * faturas. Sem doc de assinatura conta como venda direta (estado do
 * onboarding). Cortesia/demo migrados guardam `sub_…` cancelado no doc até a
 * E4 da #738, então "tem id" ≠ "assinatura viva": adiar, cancelar e sincronizar
 * só para venda direta. A troca de tipo aparece também com `partner_transition`
 * pendente, para desfazê-la.
 *
 * Cada diálogo chama o seu callable; todos os de dinheiro/assinatura exigem no
 * servidor `ADMIN_PERMISSIONS.manageSubscriptions` ou `manageCourtesy`
 * (ROOT/FINANCE, `apps/functions/src/auth/permissions.ts`) — a faixa não
 * esconde botão por papel, então outro papel admin recebe `permission-denied`.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (rota `/tenants/:id`, aba Cobrança,
 * menu Tenants; spec `docs/screens/tenant-detail-admin.md`), admin web.
 *
 * Dados: `Tenant` (`status`, `countryCode`) e `Subscription`
 * (`SubscriptionSchema`, `packages/types/src/schemas.ts`): `commercial_mode`,
 * `external_subscription_id`, `external_provider` (`appstore` e `googleplay`
 * escondem as ações de gateway, #942/#969), `status`, `courtesy.category`,
 * `discount.lifetime_free_category` (legado), `partner_transition`.
 *
 * Histórico:
 *  - 2026-08-29 14:00 +0200 — (#289) link de pagamento.
 *  - 2026-09-25 11:11 +0200 — (#738) cortesia como tipo comercial.
 *  - 2026-09-25 17:32 +0200 — (#743) troca de tipo comercial (9.18).
 *  - 2026-09-26 19:49 +0200 — (#679) link bloqueado em venda assistida não paga.
 *  - 2026-09-27 17:30 +0200 — (#761) comparações pelas constantes `CommercialMode`.
 *  - 2026-10-10 14:40 +0200 — (#798) rota, menu e permissões (#796).
 *  - 2026-10-12 12:30 +0200 — (#942) assinatura da App Store sem ações de
 *    gateway (cortesia, prorrogar, cancelar, sincronizar, link, tipo comercial).
 *  - 2026-10-13 10:42 +0200 — (#935) botão "Visão geral de cobrança" (item 23,
 *    único link de entrada de `/billing/overview/:tenantId`); prorrogação
 *    recebe o fuso do tenant (item 6).
 *  - 2026-10-13 17:20 +0200 — (#961) cancelamento agendado esconde "Prorrogar
 *    vencimento" e "Cancelar assinatura" (o card mostra o aviso).
 *  - 2026-10-14 15:17 +0200 — (#969, M4) assinatura do Google Play esconde as
 *    mesmas ações de gateway que a da App Store.
 */
export function BillingActions({
  tenant,
  subscription,
  onCheckoutLinkCreated,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState<OpenDialog>(null);
  const isDeleted = tenant.status === 'DELETED';
  const isActive = tenant.status === 'ACTIVE';
  // #679 F7: cortesia é tipo comercial (gravado, ou o cupom legado até a E1).
  // Conceder só se aplica a venda direta; demo e parceiro não mostram nenhum
  // dos dois botões — demo sai pelo interruptor de demo.
  const commercialMode = subscription ? effectiveCommercialMode(subscription) : null;
  const isCourtesy = commercialMode === CommercialMode.COURTESY;
  // Sem doc de assinatura conta como venda direta (é o estado do onboarding).
  // Cortesia e demo migrados guardam o `sub_…` cancelado no doc até a E4 — o
  // sinal do gate dos apps antigos —, então "tem id" não quer dizer "tem
  // assinatura viva": adiar, cancelar e sincronizar só fazem sentido para
  // venda direta, e o servidor recusa os três fora dela (#679 F7).
  const isDirect = commercialMode === null || commercialMode === CommercialMode.DIRECT;
  // #942 — assinatura da App Store (IAP do iOS): a Apple cobra, renova,
  // cancela e reembolsa. Cortesia (cancelaria no gateway), prorrogar, cancelar,
  // sincronizar, link de pagamento e troca de tipo comercial falam com
  // Stripe/Asaas e não se aplicam — somem para não oferecer o que o servidor
  // não tem como fazer. Suspender/reativar/excluir e faturas continuam.
  // Auditoria 2026-10-14 15:17 +0200 (#969, M4): a assinatura do Google Play (self-service no Android) tem a
  // mesma natureza — o Google cobra, renova, cancela e reembolsa —, mas só `appstore` escondia as ações e o
  // admin oferecia cortesia/prorrogar/cancelar/sincronizar/link que o servidor não tem como fazer na Play.
  const isStoreBilled =
    subscription?.external_provider === PaymentProvider.APPSTORE
    || subscription?.external_provider === PaymentProvider.GOOGLEPLAY;
  // #961 — fim já marcado no gateway (ACTIVE + `cancelled_at`): cancelar de novo
  // não muda nada e prorrogar não faz sentido para quem não será mais cobrado.
  // Antes os dois botões reapareciam porque a condição olhava só o status e o id.
  const cancelScheduled = isCancellationScheduled(subscription);
  // #289 — o guard "1 tenant = 1 subscription" recusaria no servidor; o botão
  // desabilitado precisa DIZER isso, senão o operador fica sem saber por que
  // não consegue gerar o link. CANCELLED não conta: re-assinar é o caso comum.
  const hasLiveSubscription =
    isDirect &&
    subscription?.external_subscription_id != null &&
    subscription.status !== 'CANCELLED';
  // #679 — venda assistida AINDA NÃO PAGA paga pela composição NEGOCIADA, pelo
  // link da venda no CRM. O link daqui cobra o BASE de lista e o servidor o
  // recusa (`assisted_sale_use_link`); o botão diz o porquê antes do clique.
  // O predicado é o MESMO do servidor: depois de paga, a venda acabou, e o
  // cliente que cancelou volta por este link.
  const isAssistedSale = isUnpaidAssistedSale(tenant, subscription);
  const lifetimeFreeCategory =
    subscription?.courtesy?.category ?? subscription?.discount?.lifetime_free_category ?? null;
  // "Conceder cortesia" fica habilitado para toda venda direta: o callable
  // grava COURTESY e, se houver assinatura paga, cancela-a no gateway (#679
  // F7). Sem precondição na UI — as recusas vêm do servidor.
  const close = (): void => {
    setOpen(null);
  };

  const invoicesHref = `/billing/invoices/${tenant.id}`;
  // #935 (item 23): a visão geral de cobrança do tenant não tinha nenhum link
  // de entrada — só se chegava digitando a URL.
  const overviewHref = `/billing/overview/${tenant.id}`;

  return (
    <>
      {/* Linha única de ações com rolagem horizontal: muitas ações cabem
          numa faixa só, sem quebrar em várias linhas. `width: max-content`
          mantém o tamanho natural de cada botão; o pai rola no overflow. */}
      <div style={{ overflowX: 'auto', paddingBottom: '4px' }}>
        <div style={{ display: 'flex', gap: '8px', width: 'max-content' }}>
          {isDeleted ? (
        <Button
          variant="primary"
          iconName="undo"
          onClick={(): void => {
            setOpen('restore');
          }}
        >
          {t('tenantDetail.actions.restoreDialog.button')}
        </Button>
      ) : isActive ? (
        <Button
          iconName="lock-private"
          onClick={(): void => {
            setOpen('suspend');
          }}
        >
          {t('tenantDetail.actions.suspendButton')}
        </Button>
      ) : (
        <Button
          variant="primary"
          iconName="unlocked"
          onClick={(): void => {
            setOpen('reactivate');
          }}
        >
          {t('tenantDetail.actions.reactivateButton')}
        </Button>
      )}

      {!isDeleted && (
        <Button
          iconName="remove"
          onClick={(): void => {
            setOpen('delete');
          }}
        >
          {t('tenantDetail.actions.deleteDialog.button')}
        </Button>
      )}

      <Button
        iconName="file"
        href={invoicesHref}
        onFollow={(e): void => {
          e.preventDefault();
          void navigate(invoicesHref);
        }}
      >
        {t('tenantDetail.actions.markFirstInvoicePaid')}
      </Button>

      <Button
        iconName="view-full"
        href={overviewHref}
        onFollow={(e): void => {
          e.preventDefault();
          void navigate(overviewHref);
        }}
      >
        {t('billing.overview.title')}
      </Button>

      {subscription && !isDeleted && !isStoreBilled && (commercialMode === CommercialMode.DIRECT || isCourtesy) ? (
        isCourtesy ? (
          <Button
            iconName="star-filled"
            onClick={(): void => {
              setOpen('removeLifetimeFree');
            }}
          >
            {t('billing.lifetimeFree.remove.openButton')}
          </Button>
        ) : (
          <Button
            iconName="star"
            onClick={(): void => {
              setOpen('applyLifetimeFree');
            }}
          >
            {t('billing.lifetimeFree.apply.openButton')}
          </Button>
        )
      ) : null}

      {isDirect &&
      !isStoreBilled &&
      subscription?.external_subscription_id &&
      subscription.status !== 'CANCELLED' &&
      !cancelScheduled &&
      !isDeleted ? (
        <Button
          iconName="calendar"
          onClick={(): void => {
            setOpen('postponeDueDate');
          }}
        >
          {t('billing.postpone.openButton')}
        </Button>
      ) : null}

      {isDirect &&
      !isStoreBilled &&
      subscription?.external_subscription_id &&
      subscription.status !== 'CANCELLED' &&
      !cancelScheduled &&
      !isDeleted ? (
        <Button
          iconName="close"
          onClick={(): void => {
            setOpen('cancelSubscription');
          }}
        >
          {t('billing.cancelSubscription.openButton')}
        </Button>
      ) : null}

      {subscription && isDirect && !isStoreBilled && !isDeleted ? (
        <Button
          iconName="refresh"
          onClick={(): void => {
            setOpen('reconcileSubscription');
          }}
        >
          {t('billing.reconcile.openButton')}
        </Button>
      ) : null}

      {/* #289 — link de pagamento. Fica desabilitado com assinatura VIVA
          porque o guard "1 tenant = 1 subscription" recusaria no servidor;
          melhor o botão explicar do que o clique falhar. Sub CANCELLED não
          conta: re-assinar é o caso comum de suporte, e o core libera. */}
      {!isDeleted && !isStoreBilled ? (
        <Button
          iconName="external"
          disabled={hasLiveSubscription || !isDirect || isAssistedSale}
          {...(!isDirect
            ? { disabledReason: t('billing.checkoutLink.disabledNotDirect') }
            : hasLiveSubscription
              ? { disabledReason: t('billing.checkoutLink.disabledHasSubscription') }
              : isAssistedSale
                ? { disabledReason: t('billing.checkoutLink.disabledAssistedSale') }
                : {})}
          onClick={(): void => {
            setOpen('checkoutLink');
          }}
        >
          {t('billing.checkoutLink.openButton')}
        </Button>
      ) : null}

      {/* #743 (9.18) — parceiro ↔ venda direta ↔ cortesia. Aparece também com
          troca pendente, para desfazê-la. */}
      {subscription &&
      !isDeleted &&
      !isStoreBilled &&
      commercialMode !== null &&
      (targetsFor(commercialMode).length > 0 || subscription.partner_transition) ? (
        <Button
          iconName="share"
          onClick={(): void => {
            setOpen('commercialMode');
          }}
        >
          {subscription.partner_transition
            ? t('billing.commercialMode.pendingButton')
            : t('billing.commercialMode.openButton')}
        </Button>
      ) : null}
        </div>
      </div>

      {subscription ? (
        <ChangeCommercialModeDialog
          open={open === 'commercialMode'}
          onClose={close}
          tenantId={tenant.id}
          tenantName={tenant.name}
          countryCode={tenant.countryCode ?? ''}
          subscription={subscription}
        />
      ) : null}

      <CreateCheckoutLinkDialog
        open={open === 'checkoutLink'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
        {...(onCheckoutLinkCreated ? { onCreated: onCheckoutLinkCreated } : {})}
      />
      <SuspendDialog
        open={open === 'suspend'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
      />
      <CancelSubscriptionDialog
        open={open === 'cancelSubscription'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
      />
      <ReactivateDialog
        open={open === 'reactivate'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
      />
      <DeleteTenantDialog
        open={open === 'delete'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
      />
      <RestoreTenantDialog
        open={open === 'restore'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
      />
      <ApplyLifetimeFreeDialog
        open={open === 'applyLifetimeFree'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
      />
      <RemoveLifetimeFreeDialog
        open={open === 'removeLifetimeFree'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
        currentCategory={lifetimeFreeCategory}
      />
      <ReconcileSubscriptionDialog
        open={open === 'reconcileSubscription'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
      />
      <PostponeDueDateDialog
        open={open === 'postponeDueDate'}
        onClose={close}
        tenantId={tenant.id}
        tenantName={tenant.name}
        timeZone={tenant.timeZone}
        subscription={subscription ?? null}
      />
    </>
  );
}
